import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { boardApi, fileApi, reviewApi, userApi } from '@/api/endpoints';
import { useAuthStore } from '@/store/authStore';
import { Alert, Empty, Loading } from '@/components/common/State';
import { AddressSearch } from '@/components/common/AddressSearch';
import { isAddressFilled } from '@/utils/postcode';
import { PasswordInput } from '@/components/common/PasswordInput';
import Avatar from '@/components/common/Avatar';
import MediaAttach, { MediaThumbs } from '@/components/common/MediaAttach';
import { useMediaUpload } from '@/components/common/useMediaUpload';
import { toMediaInputs, type DraftMedia } from '@/utils/media';
import { POST_STATUS_LABEL, formatDate, stars } from '@/utils/format';

type Tab = 'profile' | 'addresses' | 'reviews' | 'posts';

const TABS: [Tab, string][] = [
  ['profile', '내 정보'],
  ['addresses', '배송지'],
  ['reviews', '내 리뷰'],
  ['posts', '내 문의'],
];

export default function MyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) ?? 'profile';
  const orderItemId = searchParams.get('orderItemId');

  return (
    <>
      <h1 className="panel-title">마이페이지</h1>

      <div className="tabs">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={tab === key ? 'is-active' : undefined}
            onClick={() => setSearchParams({ tab: key })}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && <ProfileTab />}
      {tab === 'addresses' && <AddressTab />}
      {tab === 'reviews' && <ReviewTab presetOrderItemId={orderItemId ? Number(orderItemId) : null} />}
      {tab === 'posts' && <PostTab />}
    </>
  );
}

function ProfileTab() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: user?.name ?? '',
    nickname: user?.nickname ?? '',
    phone: user?.phone ?? '',
  });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '' });

  const update = useMutation({
    mutationFn: () => userApi.update(form),
    onSuccess: (updated) => {
      setUser(updated);
      setFeedback('저장했습니다.');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const changePassword = useMutation({
    mutationFn: () => userApi.changePassword(passwords),
    onSuccess: () => {
      setPasswords({ currentPassword: '', newPassword: '' });
      setFeedback('비밀번호를 변경했습니다. 다시 로그인해 주세요.');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  if (!user) return <Loading />;

  return (
    <div className="card" style={{ maxWidth: 520 }}>
      <div className="card__body">
        {feedback && <Alert tone="info">{feedback}</Alert>}

        <ProfilePhoto />

        <div className="field">
          <label>이메일</label>
          <input className="input" value={user.email} disabled />
        </div>
        <div className="field">
          <label htmlFor="name">이름</label>
          <input
            id="name"
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="nickname">닉네임</label>
          <input
            id="nickname"
            className="input"
            value={form.nickname}
            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="phone">휴대폰</label>
          <input
            id="phone"
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => update.mutate()}
          disabled={update.isPending}
        >
          저장
        </button>

        {user.provider === 'LOCAL' && (
          <>
            <hr style={{ margin: '28px 0', border: 0, borderTop: '1px solid var(--gray-200)' }} />
            <h2 style={{ fontSize: 16 }}>비밀번호 변경</h2>
            <div className="field">
              <label htmlFor="current">현재 비밀번호</label>
              <PasswordInput
                id="current"
                autoComplete="current-password"
                value={passwords.currentPassword}
                onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="new">새 비밀번호</label>
              <PasswordInput
                id="new"
                autoComplete="new-password"
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
              />
            </div>
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => changePassword.mutate()}
              disabled={changePassword.isPending}
            >
              비밀번호 변경
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const PROVIDER_LABEL: Record<string, string> = { GOOGLE: '구글', NAVER: '네이버', KAKAO: '카카오' };

/**
 * 프로필 사진. 자체 가입 회원만 직접 올리고 지울 수 있다.
 * 소셜 회원은 로그인할 때 제공자에게서 받은 사진을 쓰고, 동의하지 않았으면 기본 이미지가 보인다.
 */
function ProfilePhoto() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // 파일을 고르면 바로 올리고 저장한다. null 이면 사진을 지워 기본 이미지로 돌린다.
  const save = useMutation({
    mutationFn: async (file: File | null) => {
      const url = file ? (await fileApi.upload(file, 'profiles')).url : '';
      return userApi.update({ profileImageUrl: url });
    },
    onSuccess: (updated) => {
      setUser(updated);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!user) return null;
  const isLocal = user.provider === 'LOCAL';

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 올릴 수 있습니다.');
      return;
    }
    save.mutate(file);
  };

  return (
    <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <Avatar src={user.profileImageUrl} size={72} alt="프로필 사진" />
      <div>
        {isLocal ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => inputRef.current?.click()}
              disabled={save.isPending}
            >
              {save.isPending ? '저장 중…' : '사진 변경'}
            </button>
            {user.profileImageUrl && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => save.mutate(null)}
                disabled={save.isPending}
              >
                기본 이미지로
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </div>
        ) : (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {PROVIDER_LABEL[user.provider] ?? user.provider} 계정의 프로필 사진을 사용합니다.
          </p>
        )}
        {error && (
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--danger)' }}>{error}</p>
        )}
      </div>
    </div>
  );
}

function AddressTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['addresses'], queryFn: userApi.addresses });
  const [form, setForm] = useState({
    alias: '',
    recipient: '',
    phone: '',
    zipcode: '',
    address1: '',
    address2: '',
    isDefault: false,
  });
  const [feedback, setFeedback] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => userApi.addAddress(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      setForm({ alias: '', recipient: '', phone: '', zipcode: '', address1: '', address2: '', isDefault: false });
      setFeedback('배송지를 추가했습니다.');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const remove = useMutation({
    mutationFn: (addressId: number) => userApi.deleteAddress(addressId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['addresses'] }),
  });

  if (isLoading) return <Loading />;

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(0,1fr) minmax(0,360px)' }}>
      <div>
        {data?.length === 0 && <Empty title="등록된 배송지가 없습니다." />}
        {data?.map((address) => (
          <div className="card" key={address.addressId} style={{ marginBottom: 12 }}>
            <div className="card__body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>
                  {address.alias ?? '배송지'}{' '}
                  {address.isDefault && <span className="badge badge--info">기본</span>}
                </strong>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm muted"
                  onClick={() => remove.mutate(address.addressId)}
                >
                  삭제
                </button>
              </div>
              <p style={{ margin: '8px 0 0' }}>
                {address.recipient} · {address.phone}
              </p>
              <p className="muted" style={{ margin: 0 }}>
                [{address.zipcode}] {address.address1} {address.address2}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card__body">
          <h2 style={{ fontSize: 16, marginTop: 0 }}>배송지 추가</h2>
          {feedback && <Alert tone="info">{feedback}</Alert>}
          {(
            [
              ['alias', '별칭 (집/회사)'],
              ['recipient', '받는 분 *'],
              ['phone', '연락처 *'],
            ] as [keyof typeof form, string][]
          ).map(([key, label]) => (
            <div className="field" key={key}>
              <label htmlFor={key}>{label}</label>
              <input
                id={key}
                className="input"
                value={String(form[key])}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </div>
          ))}
          <AddressSearch
            required
            zipcode={form.zipcode}
            address1={form.address1}
            detailInputId="address2"
            onSelect={(picked) => setForm((prev) => ({ ...prev, ...picked, address2: '' }))}
          />
          <div className="field">
            <label htmlFor="address2">상세 주소</label>
            <input
              id="address2"
              className="input"
              value={form.address2}
              onChange={(e) => setForm({ ...form, address2: e.target.value })}
            />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
            />
            기본 배송지로 지정
          </label>
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => {
              if (!isAddressFilled(form)) {
                setFeedback('주소 찾기로 주소를 입력해 주세요.');
                return;
              }
              add.mutate();
            }}
            disabled={add.isPending}
          >
            추가
          </button>
        </div>
      </div>
    </div>
  );
}

const MAX_REVIEW_MEDIA = 5;

function ReviewTab({ presetOrderItemId }: { presetOrderItemId: number | null }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['myReviews'], queryFn: () => reviewApi.mine({ size: 20 }) });
  const [form, setForm] = useState({
    orderItemId: presetOrderItemId ?? 0,
    rating: 5,
    title: '',
    content: '',
  });
  const [media, setMedia] = useState<DraftMedia[]>([]);
  const uploader = useMediaUpload('reviews');
  const [feedback, setFeedback] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const uploaded = await uploader.upload(media);
      setMedia(uploaded);
      return reviewApi.create({ ...form, media: toMediaInputs(uploaded) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myReviews'] });
      setForm({ orderItemId: 0, rating: 5, title: '', content: '' });
      setMedia([]);
      setFeedback('리뷰를 등록했습니다.');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  if (isLoading) return <Loading />;

  return (
    <>
      {presetOrderItemId !== null && (
        <div className="card" style={{ marginBottom: 20, maxWidth: 560 }}>
          <div className="card__body">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>리뷰 작성</h2>
            {feedback && <Alert tone="info">{feedback}</Alert>}
            <div className="field">
              <label htmlFor="rating">별점</label>
              <select
                id="rating"
                className="select"
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
              >
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {stars(n)} ({n}점)
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="title">제목</label>
              <input
                id="title"
                className="input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="content">내용 *</label>
              <textarea
                id="content"
                className="textarea"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
              />
            </div>
            <MediaAttach value={media} onChange={setMedia} max={MAX_REVIEW_MEDIA} progress={uploader.progress} />

            <button
              type="button"
              className="btn btn--primary"
              onClick={() => create.mutate()}
              disabled={create.isPending || !form.content}
            >
              등록
            </button>
          </div>
        </div>
      )}

      {data?.content.length === 0 ? (
        <Empty title="작성한 리뷰가 없습니다." />
      ) : (
        data?.content.map((review) => (
          <div className="card" key={review.reviewId} style={{ marginBottom: 12 }}>
            <div className="card__body">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{review.productName}</strong>
                <span className="muted">{formatDate(review.createdAt)}</span>
              </div>
              <div className="stars">{stars(review.rating)}</div>
              <p style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{review.content}</p>
              <MediaThumbs media={review.media} />
            </div>
          </div>
        ))
      )}
    </>
  );
}

function PostTab() {
  const { data, isLoading } = useQuery({ queryKey: ['myPosts'], queryFn: () => boardApi.mine({ size: 20 }) });

  if (isLoading) return <Loading />;
  if (!data || data.content.length === 0) return <Empty title="작성한 문의가 없습니다." />;

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>구분</th>
            <th>제목</th>
            <th>상태</th>
            <th>작성일</th>
          </tr>
        </thead>
        <tbody>
          {data.content.map((post) => (
            <tr key={post.postId}>
              <td>{post.boardType === 'QNA' ? '상품문의' : 'VoC'}</td>
              <td className="wrap">
                <a href={`/boards/${post.postId}?boardType=${post.boardType}`}>{post.title}</a>
              </td>
              <td>{POST_STATUS_LABEL[post.status]}</td>
              <td>{formatDate(post.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
